/**
 * Bot constants — the vocabulary an AI player is described in, and the default policy a room
 * fills itself with them under. Types and tuned numbers only: nothing here decides anything, and
 * no bot exists yet.
 *
 * The matchmaker, the room host and the operator console all read these, so every value is plain
 * data with no runtime dependency beyond the simulation constants it is quoted against.
 *
 * ON THE WIRE THERE ARE NO BOTS. A bot joins a room as a player, appears in the leaderboard as a
 * player and is replicated as a player — `packages/shared/src/protocol` gains nothing from this
 * file and must not. The bot/human split is server-side bookkeeping for operators (see
 * `RoomSummary.humanCount` / `.botCount`), not a flag a client can read: a client that could
 * tell the two apart would play differently against each, and the whole point of topping a quiet
 * room up is that it does not feel topped up.
 *
 * Every number carries a one-line reason. If you change one, change the reason with it.
 */

import type { BotSkillTier, BotTierKnobs, RoomSettings } from '../types/index.js';
import { DEATHMATCH_ROUND_SECONDS } from './deathmatch.js';
import { UNRESTRICTED_ROOM_ACCESS } from './access.js';
import { massToRadius } from './mass.js';
import {
  DEFAULT_ROOM_PLAYERS,
  STARTING_MASS,
  TICK_RATE_HZ,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from './simulation.js';
import { DEFAULT_VIEW_ASPECT, interestRadiusForView } from './view.js';

/**
 * The ceiling on how far any bot may see: exactly what the server replicates to a fresh player
 * on a reference 16:9 screen (`interestRadiusForView`).
 *
 * It used to be INTEREST_RADIUS, back when that was a flat 900 for everybody. Replication is now
 * per viewer — a player who has grown is replicated further than one who has not — so a bot
 * pinned to the *widest* of those would see past what a client in its place is told, which is
 * the one thing that would make a bot detectable as one. Pinned to the *narrowest* it never
 * does, at any size, and the tier fractions below keep the numbers they were tuned at.
 */
export const BOT_MAX_PERCEPTION_RADIUS = interestRadiusForView(
  massToRadius(STARTING_MASS),
  DEFAULT_VIEW_ASPECT,
);

/**
 * The tiers, in the fixed order anything that draws one iterates them in.
 *
 * Order is load-bearing rather than cosmetic: a tier is picked by walking this list against a
 * single draw from the room's seeded stream, so two hosts replaying the same seed must walk it
 * the same way. Object key order would do the same job today and would stop doing it the first
 * time somebody re-sorted the table below.
 */
export const BOT_TIER_ORDER: readonly BotSkillTier[] = ['novice', 'casual', 'skilled', 'expert'];

/**
 * THE SKILL TABLE. One record of knobs per tier — everything that makes a novice a novice lives
 * here, so a bot brain is one implementation reading numbers rather than four implementations.
 *
 * The knobs are deliberately about *perception and hesitation*, not about permissions. A better
 * bot decides more often, sees a little further, aims straighter and mis-reads a threat less
 * often; it never gets information the simulation would not replicate to a human in its place.
 * That is what keeps "the bots are cheating" from being true — see `perceptionRadius`.
 */
export const BOT_SKILL_TIERS: Record<BotSkillTier, BotTierKnobs> = {
  /** Drives into spikes, chases what it cannot eat, and is mostly here to be eaten. */
  novice: {
    // 600 ms at TICK_RATE_HZ: about 1.7 decisions a second, so it is steering at where you were
    // a blob-length ago. A slow bot is a bot that keeps aiming at your last position.
    reactionTicks: 12,
    // 0.55 of BOT_MAX_PERCEPTION_RADIUS — it reacts to a chaser roughly half a second after the
    // human watching the same screen would have.
    perceptionRadius: 0.55 * BOT_MAX_PERCEPTION_RADIUS,
    // 0.35 rad is 20 degrees: at 300 units away that is a 100-unit miss, wider than the blob.
    aimErrorRadians: 0.35,
    // One decision in four ranks a predator as prey; at 1.7 decisions a second that is a bad
    // read every 2.4 s, which is what "fearless in the wrong direction" looks like.
    threatMisreadChance: 0.25,
    // Steers off a spike only a quarter as hard as it steers toward its target, so a spike
    // between it and a pellet is a spike it drives into.
    spikeAvoidance: 0.25,
    // One decision in ten is a bolt the wrong way — the flinch that gets a novice caught.
    panicChance: 0.1,
    // Zero: a pure uniform draw, which is exactly what rock-paper-scissors gives a human who
    // has never seen their opponent play.
    duelAccuracy: 0,
    // Almost entirely a pellet grazer; it hunts only when something edible runs into it.
    pelletGreed: 0.85,
  },

  /** The median player: competent, distractable, beatable. Most of a room is this. */
  casual: {
    // 350 ms — roughly a human's visual reaction time plus the moment it takes to commit.
    reactionTicks: 7,
    perceptionRadius: 0.75 * BOT_MAX_PERCEPTION_RADIUS,
    // 10 degrees: a miss you can see but not one you can walk away from.
    aimErrorRadians: 0.18,
    threatMisreadChance: 0.12,
    spikeAvoidance: 0.6,
    panicChance: 0.05,
    duelAccuracy: 0.15,
    pelletGreed: 0.65,
  },

  /** Plays the map: takes duels it can afford, farms the quiet corners, punishes a bad split. */
  skilled: {
    // 200 ms, four ticks — at the floor of human reaction rather than under it.
    reactionTicks: 4,
    // The full BOT_MAX_PERCEPTION_RADIUS: it sees what the client in its place is told, no more.
    perceptionRadius: BOT_MAX_PERCEPTION_RADIUS,
    // Under 5 degrees; at 300 units that is a 24-unit miss, inside a fresh player's radius.
    aimErrorRadians: 0.08,
    threatMisreadChance: 0.04,
    spikeAvoidance: 0.9,
    panicChance: 0.015,
    duelAccuracy: 0.35,
    pelletGreed: 0.45,
  },

  /** The one on the leaderboard. Rare on purpose — see BOT_TIER_WEIGHTS. */
  expert: {
    // 100 ms, two ticks. Not one: a bot that re-decided every tick would track a human's
    // steering with no lag at all, which reads as being locked onto rather than chased.
    reactionTicks: 2,
    // Also BOT_MAX_PERCEPTION_RADIUS, and this is the ceiling for every tier — see the note on
    // that constant. A bot that saw further would be reading the room's memory instead of its
    // own view: indistinguishable from a wallhack, and the one thing that would make a bot
    // detectable as one.
    perceptionRadius: BOT_MAX_PERCEPTION_RADIUS,
    // Under 2 degrees: a miss that only matters at the far edge of its vision.
    aimErrorRadians: 0.03,
    threatMisreadChance: 0.01,
    // Never voluntarily touches a spike, though it will still be pushed into one.
    spikeAvoidance: 1,
    // Exactly zero: an expert does not flinch.
    panicChance: 0,
    duelAccuracy: 0.55,
    // Hunts by preference and grazes to stay in the band where duels are winnable.
    pelletGreed: 0.3,
  },
};

/**
 * Ceiling on `duelAccuracy`, and the reason none of the tiers above come near 1.
 *
 * A mini-game duel is hidden information: neither choice is on the wire until both are locked
 * (`toDuelSnapshot` is where that rule lives). `duelAccuracy` is therefore a bias toward the
 * non-losing answer against the opponent a bot *predicts* — from what it has seen that player
 * pick before — and never against the answer they actually gave. At 1 a bot would win every
 * duel it entered, which is only possible by reading the opponent's locked choice, which is the
 * exact exploit the secrecy rule exists to stop. Kept well under it so an expert is a player who
 * reads you, not a player who sees your cards.
 */
export const BOT_MAX_DUEL_ACCURACY = 0.6;

/**
 * The spawn mix: a room is mostly average players, with a few very good and a few very bad ones.
 *
 * 15 / 45 / 30 / 10. Casual plus skilled is three quarters of the field, so the typical
 * encounter is with somebody roughly your level; a novice turns up often enough to be a free
 * meal about one time in seven, and an expert rarely enough that meeting one is an event. In a
 * room topped up to BOT_DEFAULTS.minPlayers with 10 bots that is about one expert and one or two
 * novices — which is what a real lobby looks like.
 *
 * These sum to exactly 1 so a caller can pick a tier with a single [0, 1) draw and no
 * normalising pass. `roomSettingsSchema` accepts any non-negative weights an operator sets, so a
 * *drawn* distribution still has to normalise; this table just makes the default free. The sum
 * is pinned by test.
 */
export const BOT_TIER_WEIGHTS: Record<BotSkillTier, number> = {
  novice: 0.15,
  casual: 0.45,
  skilled: 0.3,
  expert: 0.1,
};

/**
 * The default per-room bot policy. An operator overrides any of it per room through
 * `roomSettingsPatchSchema`; this is what a room starts with.
 *
 * `minPlayers` is the target *total* participant count, humans and bots together — bots top a
 * room up to it and are removed as humans arrive, so a room never runs more of them than it has
 * to. 16 comes off the encounter rate: a player sweeps `2 * BOT_MAX_PERCEPTION_RADIUS * BASE_SPEED` of
 * world a second, so at 16 participants over 6000x6000 somebody enters your view about every
 * 3 s of travel. At 8 it is 7 s, which is a room that feels abandoned; at 32 it is under 2 s,
 * which is a room nobody can graze in.
 *
 * `maxBots` is 40% of DEFAULT_ROOM_PLAYERS — the size a room opens at, not the ROOM_MAX_PLAYERS
 * ceiling, so raising that ceiling does not quietly double how much of a room is software. Bots are here to make a quiet room playable, not to be
 * the room: past this the leaderboard is mostly software and a human's whole match is against
 * it. It is also the cheap half of the cap — a bot costs a cell in the spatial hash and a
 * decision every `reactionTicks`, but no socket and no fan-out, and fan-out is the bottleneck by
 * 40-70x.
 *
 * THE COOLDOWNS ARE ASYMMETRIC, and that is the whole point of them. A room that added and
 * removed bots the instant its human count moved would thrash a player in and out of the
 * leaderboard every time somebody rage-quit and reconnected. Adding is quick — 1 s, because an
 * under-filled room is the problem being fixed — and removing is slow — 10 s, because a room
 * briefly one over `minPlayers` costs nothing, while a bot deleted mid-chase is a player
 * vanishing in front of you.
 */
export const BOT_DEFAULTS = {
  enabled: true,
  minPlayers: 16,
  maxBots: Math.round(0.4 * DEFAULT_ROOM_PLAYERS),
  /** 1 s at TICK_RATE_HZ. */
  addCooldownTicks: TICK_RATE_HZ,
  /** 10 s at TICK_RATE_HZ. */
  removeCooldownTicks: 10 * TICK_RATE_HZ,
} as const;

/**
 * Longest a pool name may be, leaving BOT_NAME_SUFFIX_RESERVE characters of the display-name
 * budget for the numeric suffix a collision adds. DISPLAY_NAME_MAX_LENGTH is 16 and a room caps
 * at ROOM_MAX_PLAYERS participants, so three digits is more suffix than any room can need; the fourth
 * character is the margin that keeps this true if either bound moves. Pinned by test.
 */
export const BOT_NAME_SUFFIX_RESERVE = 4;
export const BOT_NAME_MAX_LENGTH = 12;

/**
 * The names bots draw from, in a fixed order.
 *
 * Fixed and ordered because the draw has to be reproducible: a room picks an index from its own
 * seeded stream, and appends the lowest free integer when the name it drew is already taken. Two
 * hosts replaying the same seed therefore name their bots identically, which is what keeps a bot
 * out of the list of reasons two hosts could disagree.
 *
 * Every entry reads like something a person typed into the name box, satisfies
 * `displayNameSchema` and is at most BOT_NAME_MAX_LENGTH characters. That is not decoration:
 * bots sit in the same leaderboard as humans and a pool of `Bot_07`s would announce exactly what
 * this file says the client must never be able to tell.
 */
export const BOT_NAME_POOL: readonly string[] = [
  'Nibbles',
  'Chomper',
  'Blobby',
  'Munch',
  'Gobble',
  'Snacky',
  'BigTuna',
  'Orbit',
  'Pip',
  'Quark',
  'Bubbles',
  'Wobble',
  'Pudge',
  'Yolk',
  'Dot',
  'Nom',
  'Cheeks',
  'Mochi',
  'Pebble',
  'Tumble',
  'Squish',
  'Bean',
  'Crumb',
  'Puddle',
  'Marble',
  'Waffle',
  'Gumbo',
  'Nugget',
  'Peanut',
  'Tofu',
  'Biscuit',
  'Dumpling',
  'Noodle',
  'Ravioli',
  'Pickle',
  'Donut',
  'Muffin',
  'Pancake',
  'Cupcake',
  'Jellybean',
  'Meatball',
  'Butterball',
  'Sprout',
  'Turnip',
  'Radish',
  'Cashew',
  'Pistachio',
  'Walnut',
  'Almond',
  'Hazel',
  'Olive',
  'Plum',
  'Mango',
  'Kiwi',
  'Guava',
  'Melon',
  'Berry',
  'Fig',
  'Nectar',
  'Sorbet',
  'Gelato',
  'Custard',
  'Pudding',
  'Sprinkle',
];

/**
 * The settings a room is created with, given its player cap.
 *
 * One place rather than one per caller: the worker reports these on `GET /rooms/:id`, the admin
 * router hands them to an operator to edit, and a test builds them to stand a room up. Three
 * copies of the same default policy would be three things to forget to change.
 *
 * `tierWeights` is a fresh copy on every call because `RoomSettings` is a mutable, per-room
 * record — an operator editing one room's mix must not edit BOT_TIER_WEIGHTS itself.
 */
export function defaultRoomSettings(maxPlayers: number = DEFAULT_ROOM_PLAYERS): RoomSettings {
  return {
    // The game every figure in this repository was measured in. A room plays rounds because it
    // was declared to on the roster, never by omission.
    mode: 'classic',
    // Read only by a deathmatch room, so this is what a room gets the moment one is declared out
    // of these defaults rather than something a classic room is paying for. See
    // `RoomSettings.roundSeconds`; 0 turns it off.
    roundSeconds: DEATHMATCH_ROUND_SECONDS,
    maxPlayers,
    botsEnabled: BOT_DEFAULTS.enabled,
    minPlayers: Math.min(BOT_DEFAULTS.minPlayers, maxPlayers),
    maxBots: Math.min(BOT_DEFAULTS.maxBots, maxPlayers),
    tierWeights: { ...BOT_TIER_WEIGHTS },
    // A room that refuses nobody. Access rules are a decision an operator makes about one room
    // (or about a host's defaults, which is a decision about every room it invents) — never
    // something a room acquires by omission.
    access: { ...UNRESTRICTED_ROOM_ACCESS },
    // The walls every figure in this repository was measured inside. A room is given a different
    // size deliberately, on its roster declaration or on a host's defaults — never by omission.
    worldWidth: WORLD_WIDTH,
    worldHeight: WORLD_HEIGHT,
  };
}
